import { currentBrowserInfo } from "./../createjs-utilities/BrowserInfo";
import AbstractLoader = createjs.AbstractLoader;

type CombinedLoadQueue = createjs.LoadQueue & {
    _sendError: Function,
    _addItem: Function,
    _createLoadItem: Function,
    _createLoader: Function,
    _loadQueue: any[],
    _loadQueueBackup: any[],
    _priorityQueue: any[],
    _numItems: number,
    _updateProgress: Function,
    _plugins: any,
    _scriptOrder: any[],
    _loadedScripts: any[],

    _paused: any,
    _sendLoadStart: any,
    _loadStartWasDispatched: any,
    _numItemsLoaded: any,
    _sendComplete: any,

    _currentLoads: any[],
    _maxConnections: number,
    _canStartLoad: Function,

    _loadItem: Function,
};

//
// Monkey Patch LoadQueue#loadFile and LoadQueue#_addItem.
// - `loadFile`: Return the `AbstractLoader` instance that was created on `_addItem`
// - `_addItem`: Returns the `AbstractLoader` added to the queue.
//
// Original implementation: https://github.com/CreateJS/PreloadJS/blob/23f20e04e13829388417499385c3085f7202871d/src/preloadjs/LoadQueue.js#L1068-L1081
createjs.LoadQueue.prototype.loadFile = function(this: CombinedLoadQueue, file, loadNow, basePath): AbstractLoader {
    if (file == null) {
        const event = new createjs.ErrorEvent("PRELOAD_NO_FILE");
        this._sendError(event);
        return undefined as any;
    }
    let loadingItem = this._addItem(file, null, basePath);
    if (loadNow !== false) {
        this.setPaused(false);
    } else {
        this.setPaused(true);
    }
    return loadingItem;
};

// Original implementation: https://github.com/CreateJS/PreloadJS/blob/23f20e04e13829388417499385c3085f7202871d/src/preloadjs/LoadQueue.js#L1320-L1347
(createjs.LoadQueue.prototype as any)._addItem = function(this: CombinedLoadQueue, value: string | any, path: string, basePath: string) {
    const item = this._createLoadItem(value, path, basePath); // basePath and manifest path are added to the src.
    if (item == null) {
        return;
    } // Sometimes plugins or types should be skipped.
    const loader = this._createLoader(item);
    if (loader != null) {
        if ("plugins" in loader) {
            loader.plugins = this._plugins;
        }
        item._loader = loader;
        this._loadQueue.push(loader);
        // this._loadQueueBackup.push(loader);
        let prio = value.priority || 0;
        this._priorityQueue = this._priorityQueue || [];
        this._priorityQueue.push(prio);
        this._numItems++;
        this._updateProgress();
        // Only worry about script order when using XHR to load scripts. Tags are only loading one at a time.
        if ((this.maintainScriptOrder && item.type === createjs.LoadQueue.JAVASCRIPT) || item.maintainOrder === true) {
            this._scriptOrder.push(item);
            this._loadedScripts.push(null);
        }
    }
    return loader;
};

// Original implementation: https://github.com/CreateJS/PreloadJS/blob/23f20e04e13829388417499385c3085f7202871d/src/preloadjs/LoadQueue.js#L1124-L1181
createjs.LoadQueue.prototype.loadManifest = function(this: CombinedLoadQueue, manifest: any, loadNow, basePath) {
    let fileList: any[];
    let path: string | null = null;
    // Array-based list of items
    if (Array.isArray(manifest)) {
        if (manifest.length === 0) {
            const event = new createjs.ErrorEvent("PRELOAD_MANIFEST_EMPTY");
            this._sendError(event);
            return undefined;
        }
        fileList = manifest;
        // String-based. Only file manifests can be specified this way. Any other types will cause an error when loaded.
    } else if (typeof (manifest) === "string") {
        fileList = [
            {
                src: manifest,
                type: createjs.AbstractLoader.MANIFEST
            }
        ];
    } else if (typeof (manifest) === "object") {
        // An object that defines a manifest path
        if (manifest.src !== undefined) {
            if (manifest.type == null) {
                manifest.type = createjs.AbstractLoader.MANIFEST;
            } else if (manifest.type !== createjs.AbstractLoader.MANIFEST) {
                const event = new createjs.ErrorEvent("PRELOAD_MANIFEST_TYPE");
                this._sendError(event);
            }
            fileList = [manifest];
            // An object that defines a manifest
        } else if (manifest.manifest !== undefined) {
            fileList = manifest.manifest;
            path = manifest.path;
        }
        // Unsupported. This will throw an error.
    } else {
        const event = new createjs.ErrorEvent("PRELOAD_MANIFEST_NULL");
        this._sendError(event);
        return undefined;
    }

    let self = this;
    fileList!.forEach(function(file, i, fl) { // can't see any reason why this should not be set, but why doesn't TS catch it? TODO
        // change image type
        if ((window as any).AssetURL) { // optionally set by the game
            file = (window as any).AssetURL.convert(file); // global defined in AssetUrlConvertor.ts (BF)
            fl[i] = file; // Updating the file in the source array. May or may not be reuiqred.
        }
        self._addItem(file, path, basePath);
    });

    if (loadNow !== false) {
        this.setPaused(false);
    } else {
        this.setPaused(true);
    }

    return [];
};

let findFirstLargest = (currentResult: number, currentValue: number, currentIndex: number, array: any[]) => {
    return currentResult > currentIndex ? currentResult : currentValue > array[currentResult] ? currentIndex : currentResult;
};

(createjs.LoadQueue.prototype as any)._loadNext = function (this: CombinedLoadQueue): void {
    if (this._paused) {
        return;
    }

    // Only dispatch loadstart event when the first file is loaded.
    if (!this._loadStartWasDispatched) {
        this._sendLoadStart();
        this._loadStartWasDispatched = true;
    }

    // The queue has completed.
    if (this._numItems === this._numItemsLoaded) {
        this.loaded = true;
        this._sendComplete();

        // Load the next queue, if it has been defined.
        if (this.next && this.next.load) {
            this.next.load();
        }
    } else {
        this.loaded = false;
    }

    // Must iterate forwards to load in the right order.
    for (let i = 0; i < this._loadQueue.length; i++) {
        if (this._currentLoads.length >= this._maxConnections) {
            break;
        }
        const indexToLoad = this._priorityQueue.reduce(findFirstLargest, i);
        const loader = this._loadQueue[indexToLoad];

        // Determine if we should be only loading one tag-script at a time:
        // Note: maintainOrder items don't do anything here because we can hold onto their loaded value
        if (!this._canStartLoad(loader)) {
            continue;
        }
        this._loadQueue.splice(indexToLoad, 1);
        this._priorityQueue.splice(indexToLoad, 1);
        i--;
        this._loadItem(loader);
    }
};


createjs.RequestUtils.getTypeByExtension = function (extension) {
    if (extension == null) {
        return createjs.AbstractLoader.TEXT;
    }

    switch (extension.toLowerCase()) {
        case "jpeg":
        case "jpg":
        case "gif":
        case "png":
        case "webp":
        case "bmp":
            return createjs.AbstractLoader.IMAGE;
        case "ogg":
        case "mp3":
        case "webm":
            return createjs.AbstractLoader.SOUND;
        case "mp4":
        case "webm":
        case "ts":
            return createjs.AbstractLoader.VIDEO;
        case "json":
            return createjs.AbstractLoader.JSON;
        case "xml":
            return createjs.AbstractLoader.XML;
        case "css":
            return createjs.AbstractLoader.CSS;
        case "js":
            return createjs.AbstractLoader.JAVASCRIPT;
        case "svg":
            return createjs.AbstractLoader.SVG;
        case "bpg":
             return createjs.AbstractLoader.BINARY;
        default:
            return createjs.AbstractLoader.TEXT;
    }
};



(createjs.LoadQueue.prototype as any)._cleanLoadItem = function(loader: createjs.AbstractLoader & {_request: any}) {
    let item: any = loader.getItem();
    if (item) {
        delete item._loader;
    }
    if ((loader._request instanceof createjs.JSONLoader) || loader._request instanceof createjs.JSONPLoader) {
        loader._request.removeAllEventListeners();
        (loader._request as any)._request = null;
    } else {
        loader._request = null;
    }
};

(createjs.ImageLoader.prototype as any)._formatImage = function (successCallback: Function, errorCallback: Function) {
    let tag = this._tag;
    let URL = window.URL || (window as any).webkitURL;

    if (!this._preferXHR) {
        // document.body.removeChild(tag);
    } else if (URL) {
        let result = this.getResult(true);
        if (!result || !(result.constructor.name === "Blob" || result.constructor.name === "File")) {
            errorCallback(tag);
            return;
        }
        let objURL = URL.createObjectURL(result);
        tag.src = objURL;

        tag.addEventListener("load", this._cleanUpURL, false);
        tag.addEventListener("error", this._cleanUpURL, false);
    } else {
        tag.src = this._item.src;
    }

    if (tag.complete) {
        successCallback(tag);
    } else {
        tag.onload = createjs.proxy(function(this: any) {
            tag.onload = null;
            tag.onerror = null;
            successCallback( this._tag);
        }, this);

        tag.onerror = createjs.proxy(function(this: any) {
            tag.onload = null;
            tag.onerror = null;
            errorCallback( this._tag);
        }, this);
    }
};
